import { Navigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { brandCopyright } from '@/config/brand';
import { maintenanceEnabled, pickMessage, useMaintenance } from '@/api/maintenance';
import { AuthShell } from './components/AuthShell';
import styles from './MaintenancePage.module.css';

const STRINGS = {
  en: {
    title: 'System maintenance',
    fallback: 'We are performing scheduled maintenance. Sign-in will be back shortly.',
    until: 'Expected completion',
    auto: 'This page checks again by itself and takes you to sign in as soon as we are done.',
    retry: 'Check again',
  },
  vi: {
    title: 'Hệ thống đang bảo trì',
    fallback: 'Chúng tôi đang bảo trì định kỳ. Đăng nhập sẽ sớm hoạt động trở lại.',
    until: 'Dự kiến hoàn tất',
    auto: 'Trang này tự kiểm tra lại và chuyển bạn về trang đăng nhập ngay khi hệ thống sẵn sàng.',
    retry: 'Kiểm tra lại',
  },
} as const;

/** Fixed UI text follows the visitor's language (the notice itself comes translated from the API). */
function strings() {
  return navigator.language.toLowerCase().startsWith('vi') ? STRINGS.vi : STRINGS.en;
}

function formatUntil(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

/**
 * System-maintenance screen, built on AuthShell so it matches the sign-in pages (branded panel,
 * theme toggle, typography). The notice and the expected end come from the maintenance window that
 * staff schedule through the API; the page re-checks every 15 s and sends people back to the
 * sign-in as soon as the window is over.
 */
export default function MaintenancePage() {
  const { data, isPending, refetch, isFetching } = useMaintenance(15_000);
  const t = strings();

  // Nothing is scheduled (or the gate is off): there is nothing to wait for.
  if (!maintenanceEnabled || (!isPending && !data?.maintenance)) {
    return <Navigate to="/auth/login" replace />;
  }

  const active = data?.active;
  const message = pickMessage(active?.message);
  const until = active ? formatUntil(active.ends_at) : '';

  return (
    <AuthShell
      title={t.title}
      subtitle={<span className={styles.message}>{message || t.fallback}</span>}
      footer={brandCopyright()}
    >
      <div className={styles.stack}>
        {until ? (
          <Alert tone="warning" title={t.until}>
            {until}
          </Alert>
        ) : null}
        <p className={styles.note}>{t.auto}</p>
        <Button
          variant="secondary"
          className={styles.action}
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw size={16} aria-hidden /> {t.retry}
        </Button>
      </div>
    </AuthShell>
  );
}
